import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Heart } from "lucide-react";
import { getThemeStyles } from "@/lib/themes";

const API = "/api";

function AdSlot({ code, placeholder, height, className = "" }) {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!code || !code.trim() || !iframeRef.current) return;
    const iframe = iframeRef.current;
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><style>body{margin:0;overflow:hidden;display:flex;align-items:center;justify-content:center;}</style></head><body>${code}</body></html>`);
    doc.close();
  }, [code]);

  if (code && code.trim()) {
    return (
      <iframe
        ref={iframeRef}
        title="ad"
        className={className}
        style={{ border: "none", width: "100%", height, display: "block" }}
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      />
    );
  }
  return (
    <div className={`${className} flex items-center justify-center`} style={{ height, background: "rgba(255,255,255,0.04)", border: "1px dashed rgba(255,255,255,0.12)", borderRadius: "8px" }}>
      <span className="text-xs text-white/25 tracking-wide uppercase">{placeholder}</span>
    </div>
  );
}

export default function PublicLandingPage() {
  const { linkId } = useParams();
  const [pageData, setPageData] = useState(null);
  const [ads, setAds] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const [pageRes, adRes] = await Promise.all([
          axios.get(`${API}/public/page/${linkId}`),
          axios.get(`${API}/public/ad-settings`)
        ]);
        setPageData(pageRes.data);
        setAds(adRes.data || {});
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [linkId]);

  // Load aclib script into document head
  useEffect(() => {
    if (document.getElementById("aclib")) return;
    const script = document.createElement("script");
    script.id = "aclib";
    script.type = "text/javascript";
    script.src = "//acscdn.com/script/aclib.js";
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("aclib");
      if (existing) existing.remove();
    };
  }, []);

  const theme = pageData ? getThemeStyles(pageData.theme_preset, pageData.custom_colors) : getThemeStyles("dark_teal", null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme.background }} data-testid="public-page-loading">
        <Loader2 className="h-8 w-8 animate-spin text-white/60" />
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)" }} data-testid="public-page-error">
        <div className="text-center px-6">
          <h1 className="font-heading text-3xl font-bold mb-2 text-white">Page Not Found</h1>
          <p className="text-white/60">This support link is not valid or has been removed.</p>
        </div>
      </div>
    );
  }

  const orgName = pageData.organization_name || pageData.name || "this creator";

  return (
    <div className="min-h-screen" style={{ background: theme.background }} data-testid="public-landing-page">
      {/* Decorative blurred shapes */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${theme.glow1}, transparent)` }} />
        <div className="absolute top-1/2 -left-48 w-80 h-80 rounded-full opacity-10" style={{ background: `radial-gradient(circle, ${theme.glow2}, transparent)` }} />
        <div className="absolute -bottom-24 right-1/4 w-72 h-72 rounded-full opacity-15" style={{ background: `radial-gradient(circle, ${theme.glow3}, transparent)` }} />
      </div>

      <div className="relative z-10">
        {/* Top Ad Banner */}
        <div className="w-full" style={{ background: "rgba(255,255,255,0.03)" }} data-testid="ad-slot-top">
          <div className="max-w-5xl mx-auto px-6 py-3">
            <AdSlot code={ads.top} placeholder="Advertisement - 728x90 Leaderboard" height="90px" className="w-full" />
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Thank You Section */}
            <div className="md:col-span-2">
              {/* Glassmorphism hero card */}
              <div className="rounded-2xl p-8 md:p-12 mb-8" style={{ background: "rgba(255,255,255,0.07)", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)", border: "1px solid rgba(255,255,255,0.1)" }}>
                <div className="flex items-center gap-2 mb-6">
                  <Heart className="h-5 w-5 text-rose-400" fill="currentColor" />
                  <p className="text-sm font-medium tracking-wide uppercase text-teal-300" data-testid="public-page-tagline">
                    Your support matters
                  </p>
                </div>
                <h1
                  className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-tight mb-6"
                  data-testid="public-page-title"
                >
                  Thank you for<br />supporting
                </h1>
                <div
                  className="inline-block mb-8"
                  data-testid="public-page-org-name"
                >
                  <span className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" style={{ background: "linear-gradient(135deg, #5eead4, #a78bfa)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                    {orgName}
                  </span>
                </div>
                <p className="text-base md:text-lg text-white/60 leading-relaxed max-w-lg">
                  Your contribution helps keep things running. We genuinely appreciate every bit of support.
                </p>
              </div>

              {/* Inline Ad */}
              <div className="mt-8 mb-8" data-testid="ad-slot-inline">
                <AdSlot code={ads.inline} placeholder="Ad - 336x280 Rectangle" height="250px" className="w-full max-w-[336px]" />
              </div>
            </div>

            {/* Sidebar Ads */}
            <div className="space-y-6">
              <div data-testid="ad-slot-sidebar-1">
                <AdSlot code={ads.sidebar_1} placeholder="Ad - 300x250" height="250px" className="w-full" />
              </div>
              <div data-testid="ad-slot-sidebar-2">
                <AdSlot code={ads.sidebar_2} placeholder="Ad - 300x600 Half Page" height="600px" className="w-full" />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Ad Banner */}
        <div className="w-full" style={{ background: "rgba(255,255,255,0.03)" }} data-testid="ad-slot-bottom">
          <div className="max-w-5xl mx-auto px-6 py-3">
            <AdSlot code={ads.bottom} placeholder="Advertisement - 728x90 Leaderboard" height="90px" className="w-full" />
          </div>
        </div>

        {/* Footer */}
        <footer style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="max-w-5xl mx-auto px-6 py-6 text-center">
            <p className="text-sm text-white/40">
              Powered by <span className="font-medium text-white/60">Click Colony</span>
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
